import { InvestorDetail } from '@workspace/seedforge-features/analytics'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { FounderProfileAnalyticsData } from './types'

type Visitor = FounderProfileAnalyticsData['visitors'][number]
type SortKey = 'visits' | 'lastVisit' | 'timeSpent'
type SortDir = 'asc' | 'desc'
type TimeRange = '7' | '14' | '30' | 'all'

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
]

/**
 * `lastVisit` is a backend-formatted "Sep 9, 05:17 PM" string with no year
 * (share-link.repository.ts) — fine for display, but this reconstructs an
 * approximate Date for client-side range filtering by assuming the current
 * year, which holds for the last-30-days window this table is fed from.
 */
function parseLastVisit(lastVisit: string): Date | null {
  if (!lastVisit) return null
  const withYear = `${lastVisit} ${new Date().getFullYear()}`
  const parsed = new Date(withYear)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function parseDuration(duration: string): number {
  let total = 0
  const h = duration.match(/(\d+)h/)
  const m = duration.match(/(\d+)m/)
  const s = duration.match(/(\d+)s/)
  if (h?.[1]) total += Number.parseInt(h[1], 10) * 3600
  if (m?.[1]) total += Number.parseInt(m[1], 10) * 60
  if (s?.[1]) total += Number.parseInt(s[1], 10)
  return total
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

const ENGAGEMENT_BADGE: Record<string, { label: string; className: string }> = {
  deep_dive: {
    label: 'DEEP DIVE',
    className: 'border-[#f97316]/40 text-[#f97316]',
  },
  engaged: {
    label: 'ENGAGED',
    className: 'border-[#f97316]/40 text-[#f97316]',
  },
  quick_scan: {
    label: 'QUICK SCAN',
    className: 'border-border text-muted-foreground',
  },
  summary_only: {
    label: 'SUMMARY',
    className: 'border-border text-muted-foreground/70',
  },
}

/** Builds the mockup's "Deck 1 to 9 · Pitch 12m · Team" read summary. */
function buildReadSummary(v: Visitor): string {
  const parts: string[] = []

  const deckDoc = v.documentViewing?.find((d) => d.pageBreakdown.length > 0)
  if (deckDoc) {
    const maxPage = Math.max(...deckDoc.pageBreakdown.map((p) => p.page))
    parts.push(maxPage > 1 ? `Deck 1 to ${maxPage}` : 'Deck page 1')
  }

  const pitchSeconds = (v.sessions ?? [])
    .flatMap((s) => s.events)
    .filter((e) => e.type === 'pitch' && e.duration)
    .reduce((sum, e) => sum + parseDuration(e.duration), 0)
  if (pitchSeconds > 0) parts.push(`Pitch ${formatSeconds(pitchSeconds)}`)

  for (const section of v.sections ?? []) {
    if (section === 'profile' || section === 'pitch') continue
    parts.push(section.charAt(0).toUpperCase() + section.slice(1))
  }

  if (v.usedChat) parts.push('Asked chat')

  return parts.length > 0 ? parts.join(' · ') : '—'
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}

function identity(v: Visitor): { label: string; sublabel: string; initials: string } {
  if (v.name) {
    return {
      label: v.name,
      sublabel: v.title ? `${v.title} · ${v.firmName ?? ''}` : (v.firmName ?? ''),
      // `v.initials` is derived from email on the backend, so it stays "?"
      // for a resolved-by-name outreach identity with no gate email — the
      // name itself is the better source of initials here.
      initials: initialsFromName(v.name),
    }
  }
  if (v.email) {
    return { label: v.email, sublabel: 'gave email at the gate', initials: v.initials }
  }
  if (v.likelyForwarded) {
    return { label: 'New viewer', sublabel: 'appeared on this link', initials: '?' }
  }
  // The Link column already names the link, so the sublabel here just says
  // "no email" — no need to repeat it.
  return { label: 'Anonymous visitor', sublabel: 'no email', initials: '?' }
}

const INITIAL_VISIBLE = 10

function SortButton({
  label,
  active,
  dir,
  onClick,
  className,
}: {
  label: string
  active: boolean
  dir: SortDir
  onClick: () => void
  className?: string
}) {
  const Icon = dir === 'asc' ? ChevronUp : ChevronDown
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // Tailwind's Preflight resets `text-transform: none` on <button>, so
        // the header row's `uppercase` (set on its ancestor div) never reaches
        // this element — it must be repeated here explicitly.
        'flex shrink-0 items-center gap-1 text-left uppercase',
        active && 'text-foreground',
        className
      )}
    >
      {label}
      {active && <Icon className="size-3" />}
    </button>
  )
}

/** Zone 3 of "Share your Profile": the visitors log. */
export function VisitorsTable({
  visitors,
  onLinkClick,
}: {
  visitors: Visitor[]
  /** Scrolls to and expands that link's settings card further down the page. */
  onLinkClick?: (linkId: string) => void
}) {
  const [sortKey, setSortKey] = useState<SortKey>('lastVisit')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [linkFilter, setLinkFilter] = useState<string>('all')
  const [timeRange, setTimeRange] = useState<TimeRange>('30')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const linkNames = useMemo(
    () => Array.from(new Set(visitors.map((v) => v.linkName).filter((n): n is string => !!n))),
    [visitors]
  )

  // Aggregate visit volume per link name, to tag each row's link pill with
  // a brief signal of how much overall activity that link has seen — not
  // just this one visitor's own count.
  const linkActivity = useMemo(() => {
    const totals = new Map<string, number>()
    for (const v of visitors) {
      if (!v.linkName) continue
      totals.set(v.linkName, (totals.get(v.linkName) ?? 0) + v.visits)
    }
    return totals
  }, [visitors])

  function linkActivityTag(linkName: string | undefined): string | null {
    if (!linkName) return null
    const total = linkActivity.get(linkName) ?? 0
    if (total === 1) return 'New'
    return null
  }

  if (visitors.length === 0) return null

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const cutoff =
    timeRange === 'all' ? null : Date.now() - Number.parseInt(timeRange, 10) * 24 * 60 * 60 * 1000

  const filtered = visitors
    .filter((v) => linkFilter === 'all' || v.linkName === linkFilter)
    .filter((v) => {
      if (cutoff === null) return true
      const visited = parseLastVisit(v.lastVisit)
      return visited === null || visited.getTime() >= cutoff
    })

  const dirMul = sortDir === 'desc' ? 1 : -1
  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === 'visits') return (b.visits - a.visits) * dirMul
    if (sortKey === 'timeSpent') {
      return (parseDuration(b.timeSpent) - parseDuration(a.timeSpent)) * dirMul
    }
    const aVisited = parseLastVisit(a.lastVisit)?.getTime() ?? 0
    const bVisited = parseLastVisit(b.lastVisit)?.getTime() ?? 0
    return (bVisited - aVisited) * dirMul
  })
  const rows = showAll ? sorted : sorted.slice(0, INITIAL_VISIBLE)

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card/30">
      <div className="flex items-center justify-between border-b border-border px-6 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Visitors
        </p>
        <div className="flex items-center gap-2">
          {linkNames.length > 0 && (
            <div className="relative">
              <select
                value={linkFilter}
                onChange={(e) => setLinkFilter(e.target.value)}
                className="appearance-none rounded-full border border-border bg-card2 py-1 pl-3 pr-7 text-[11.5px] font-medium text-foreground"
              >
                <option value="all">All links</option>
                {linkNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
            </div>
          )}
          <div className="relative">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              className="appearance-none rounded-full border border-border bg-card2 py-1 pl-3 pr-7 text-[11.5px] font-medium text-foreground"
            >
              {TIME_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>
      </div>
      {/* This row's fixed-width columns (Link/Visits/Last visit/Total time)
          plus the flex-grow Who/Read columns need real desktop-table width to
          stay readable — squeezing them into a phone's viewport is what was
          causing the Who column's avatar/name to visually collide with the
          Link pill. A horizontal scroller keeps every column at its natural
          width instead, matching how DocSend/Papermark handle this table on
          mobile (scroll sideways, don't reflow into a card layout). */}
      <div className="overflow-x-auto">
        <div className="min-w-[880px]">
          <div className="flex items-center gap-3 border-b border-border px-6 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span className="flex-[2] min-w-0">Who</span>
            <span className="w-36 shrink-0">Link</span>
            <SortButton
              label="Visits"
              active={sortKey === 'visits'}
              dir={sortDir}
              onClick={() => toggleSort('visits')}
              className="w-28"
            />
            <SortButton
              label="Last visit"
              active={sortKey === 'lastVisit'}
              dir={sortDir}
              onClick={() => toggleSort('lastVisit')}
              className="w-32"
            />
            <SortButton
              label="Total time"
              active={sortKey === 'timeSpent'}
              dir={sortDir}
              onClick={() => toggleSort('timeSpent')}
              className="w-20"
            />
            <span className="flex-[1.5] shrink-0">Read</span>
            <span className="w-4 shrink-0" />
          </div>
          {rows.length === 0 && (
            <p className="px-6 py-6 text-center text-xs text-muted-foreground">
              No visitors for this link yet.
            </p>
          )}
          {rows.map((v) => {
            const id = identity(v)
            const badge = v.engagementLevel ? ENGAGEMENT_BADGE[v.engagementLevel] : undefined
            const expanded = expandedId === v.id

            const toggleExpanded = () => setExpandedId((cur) => (cur === v.id ? null : v.id))

            return (
              <div key={v.id} className="border-b border-border last:border-b-0">
                {/*
              A flat flex row, matching the header 1:1 column-for-column.
              Two <button>s share the click-to-expand behavior; the second is
              `contents` (no box of its own) so ITS children become direct
              flex items of this row, exactly like the header's flat items —
              nesting them inside one real box previously broke the flex-grow
              math (WHO and READ each grow, but split across two separate
              flex containers no longer share the same leftover-space pool,
              so their widths drifted from the header's). The link pill is a
              real sibling <button>, not nested inside either expand button.
            */}
                <div className="flex w-full items-center gap-3 px-6 py-3.5 transition-colors hover:bg-white/[0.02]">
                  <button
                    type="button"
                    onClick={toggleExpanded}
                    className="flex min-w-0 flex-[2] items-center gap-3 text-left"
                  >
                    <div
                      className={cn(
                        'flex size-8 shrink-0 items-center justify-center rounded-full text-[10px] font-medium',
                        v.name
                          ? 'bg-[#f97316]/20 text-[#f97316]'
                          : 'bg-elevated text-muted-foreground'
                      )}
                    >
                      {id.initials}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{id.label}</p>
                      <p className="truncate text-[10.5px] text-muted-foreground">{id.sublabel}</p>
                    </div>
                  </button>

                  <span className="flex w-36 shrink-0 items-center gap-1">
                    {v.linkName &&
                      (v.linkId && onLinkClick ? (
                        <button
                          type="button"
                          onClick={() => onLinkClick(v.linkId as string)}
                          className="inline-flex min-w-0 shrink items-center rounded-full border border-border px-2 py-0.5 text-[10.5px] text-muted-foreground transition-colors hover:border-primary/60 hover:text-foreground"
                          title={`Jump to "${v.linkName}" settings`}
                        >
                          <span className="truncate">{v.linkName}</span>
                        </button>
                      ) : (
                        <span
                          className="inline-flex min-w-0 shrink items-center rounded-full border border-border px-2 py-0.5 text-[10.5px] text-muted-foreground"
                          title={v.linkName}
                        >
                          <span className="truncate">{v.linkName}</span>
                        </span>
                      ))}
                    {linkActivityTag(v.linkName) === 'New' && (
                      <span
                        className="shrink-0 rounded-full border border-blue-400/40 px-1.5 py-0.5 text-[9px] leading-none text-blue-400"
                        title="Only one visit on this link so far"
                      >
                        new
                      </span>
                    )}
                  </span>

                  <button type="button" onClick={toggleExpanded} className="contents">
                    <span className="flex w-28 shrink-0 items-center gap-1.5 whitespace-nowrap text-sm text-foreground">
                      {v.visits}
                      {v.likelyForwarded ? (
                        <span
                          className="rounded-full border border-amber-400/40 px-1.5 py-0.5 text-[9px] leading-none text-amber-400"
                          title="Likely forwarded"
                        >
                          forwarded
                        </span>
                      ) : (
                        v.visits > 1 && (
                          <span className="rounded-full border border-emerald-400/40 px-1.5 py-0.5 text-[9px] leading-none text-emerald-400">
                            returned
                          </span>
                        )
                      )}
                    </span>

                    <span className="w-32 shrink-0 whitespace-nowrap text-left text-[13px] text-foreground">
                      {v.lastVisit}
                    </span>
                    <span className="w-20 shrink-0 text-left text-[13px] text-foreground">
                      {v.timeSpent}
                    </span>

                    <span className="flex min-w-0 flex-[1.5] items-center gap-2 truncate text-[12px] text-foreground">
                      {badge && (
                        <span
                          className={cn(
                            'shrink-0 rounded-md border px-1.5 py-0.5 text-[9.5px] font-semibold',
                            badge.className
                          )}
                        >
                          {badge.label}
                        </span>
                      )}
                      <span className="truncate text-muted-foreground">{buildReadSummary(v)}</span>
                    </span>

                    <ChevronDown
                      className={cn(
                        'size-4 shrink-0 text-muted-foreground transition-transform',
                        expanded && 'rotate-180'
                      )}
                    />
                  </button>
                </div>

                {expanded && (
                  <InvestorDetail
                    investor={{
                      id: v.id,
                      initials: v.initials,
                      firm:
                        v.name && v.firmName ? `${v.name} · ${v.firmName}` : (v.name ?? v.email),
                      lastVisit: v.lastVisit,
                      visits: v.visits,
                      timeSpent: v.timeSpent,
                      sections: v.sections ?? [],
                      sessions: v.sessions ?? [],
                      documentViewing: v.documentViewing,
                      linkId: v.linkId,
                    }}
                    showSessions={false}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
      {!showAll && sorted.length > INITIAL_VISIBLE && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="w-full border-t border-border py-2.5 text-center text-xs text-muted-foreground hover:text-foreground"
        >
          Show all {sorted.length} people
        </button>
      )}
    </div>
  )
}
